import moment from 'moment';

import { DROP_OFF_PROGRAMS } from '../config/service';
import { formatCurrencyNumber } from './numbers';
import GoLockerLogo from '../images/golocker-logo.svg';
import GoLockerLogoReverse from '../images/golocker-logo-reverse.svg';

/**
 * GoLocker — self-service package lockers in NYC, reached through The Return.
 *
 * A GoLocker shipping method is a THE_RETURN method (`shippingProviderId:
 * 'THE_RETURN'`, same order, QR label and webhook as the dry-cleaner "Local Drop
 * Off"), so a `shippingProviderId === THE_RETURN` check alone hands GoLocker The
 * Return's behaviour: the magenta "Trashie Local" pill, "Trashie drop-off" copy and
 * the /the-return locations call. `isGoLocker` must therefore be checked BEFORE every
 * THE_RETURN branch — see the call sites.
 */

// Brand assets in one place, so swapping in the partner's final artwork is one line.
// Both are Figma's "Go Locker logo" (section 12224:8697): the colour mark for light
// surfaces (the option card, the kiosk card) and its knock-out for the green band,
// QR sheet and carousel heading.
export const GOLOCKER_BRAND = {
  name: 'GoLocker',
  logo: GoLockerLogo,
  logoReverse: GoLockerLogoReverse,
  // Same value as --trashie-golocker-green. Needed as a literal for the inline band
  // colour the drop-off surfaces take (they already paint payload colours inline).
  color: '#64B145',
};

// The server's radius when the response does not say (it always does today).
export const GOLOCKER_DEFAULT_RADIUS_MILES = 1;

/**
 * Whether a shipping method, a /shipping-labels entry or a shipment is GoLocker.
 * Reads every place the program can arrive: a method's configuration, an entry's
 * top-level `dropOffProgram`, or a shipment's nested shippingMethod.
 */
export const isGoLocker = (source) => (
  source?.configuration?.dropOffProgram === DROP_OFF_PROGRAMS.GOLOCKER
  || source?.dropOffProgram === DROP_OFF_PROGRAMS.GOLOCKER
  || source?.shippingMethod?.configuration?.dropOffProgram === DROP_OFF_PROGRAMS.GOLOCKER
);

export const AVAILABILITY_STATUS = {
  AVAILABLE: 'AVAILABLE',
  FEW_LEFT: 'FEW_LEFT',
  FULL: 'FULL',
  UNKNOWN: 'UNKNOWN',
};

// Visual tone per status — drives the dot, the map pin and the text colour.
export const AVAILABILITY_TONES = {
  AVAILABLE: 'available',
  FEW_LEFT: 'few',
  FULL: 'full',
  UNKNOWN: 'unknown',
};

const formatSizes = (sizes) => (
  sizes ? ['M', 'L', 'XL'].map((size) => `${size} ${Number(sizes[size]) || 0}`).join(' · ') : ''
);

/**
 * The availability line for one bank (Figma G2 / E4 / E6):
 *   AVAILABLE → "Open now · M 4 · L 2 · XL 1"
 *   FEW_LEFT  → "Few left · M 0 · L 1 · XL 0"
 *   FULL      → "Full right now · try another GoLocker"
 *   UNKNOWN   → "Live availability isn't loading"
 * Returns null for a location that carries no availability at all — the preview
 * rendered from the option card's `nearestLocations` before the live call lands.
 */
export const getAvailabilityDisplay = (availability) => {
  if (!availability) return null;
  const sizes = formatSizes(availability.sizes);
  switch (availability.status) {
    case AVAILABILITY_STATUS.AVAILABLE:
      return { tone: AVAILABILITY_TONES.AVAILABLE, label: sizes ? `Open now · ${sizes}` : 'Open now' };
    case AVAILABILITY_STATUS.FEW_LEFT:
      return { tone: AVAILABILITY_TONES.FEW_LEFT, label: sizes ? `Few left · ${sizes}` : 'Few left' };
    case AVAILABILITY_STATUS.FULL:
      return { tone: AVAILABILITY_TONES.FULL, label: 'Full right now · try another GoLocker' };
    default:
      return { tone: AVAILABILITY_TONES.UNKNOWN, label: 'Live availability isn\'t loading' };
  }
};

/** The map-pin tone; a bank with no live answer yet reads as unknown (black pin). */
export const getAvailabilityTone = (availability) => (
  getAvailabilityDisplay(availability)?.tone ?? AVAILABILITY_TONES.UNKNOWN
);

/**
 * The same banks with their availability dropped to UNKNOWN ("Live availability isn't
 * loading") — what a failed /golocker call leaves on screen: every listed bank is
 * still a valid drop-off, but a stale "Open now" would mislead.
 */
export const withUnknownAvailability = (locations = []) => locations.map((location) => ({
  ...location,
  availability: { status: AVAILABILITY_STATUS.UNKNOWN, sizes: null, totalAvailable: null },
}));

// "6a" / "11p" / "6:30a" — the design's short time (Figma "Daily 6a–11p").
const formatShortTime = (time) => {
  const value = moment(time);
  if (!value.isValid()) return '';
  return `${value.minutes() ? value.format('h:mm') : value.format('h')}${value.format('a').charAt(0)}`;
};

const formatRange = ({ openingTime, closingTime }) => {
  const open = formatShortTime(openingTime);
  const close = formatShortTime(closingTime);
  return open && close ? `${open}–${close}` : '';
};

const isTodayDayOfWeek = (dayOfWeek) => moment().day() === Number(dayOfWeek) % 7;

/**
 * The hours line for a bank: "Open 24/7" when every day is 24h, "Daily 6a–11p" when
 * all seven days share one range, otherwise today's ("Today 6a–11p", "Open 24 hours
 * today", "Closed today"). '' when the bank ships no hours — unknown is not closed.
 *
 * `dayOfWeek` is 1 = Monday .. 7 = Sunday (see dropOffLocations.isTodayDayOfWeek).
 */
export const formatGoLockerHours = (openingHours) => {
  if (!Array.isArray(openingHours) || openingHours.length === 0) return '';
  const days = new Set(openingHours.map(({ dayOfWeek }) => Number(dayOfWeek) % 7));
  const everyDay = days.size === 7;

  if (everyDay && openingHours.every((h) => h.isOpen24Hours)) return 'Open 24/7';

  const ranges = openingHours.map((h) => (h.isOpen24Hours ? '24h' : formatRange(h)));
  if (everyDay && ranges[0] && ranges.every((range) => range === ranges[0])) {
    return `Daily ${ranges[0]}`;
  }

  const today = openingHours.find((h) => isTodayDayOfWeek(h.dayOfWeek));
  if (!today) return 'Closed today';
  if (today.isOpen24Hours) return 'Open 24 hours today';
  const range = formatRange(today);
  return range ? `Today ${range}` : '';
};

/** "0.2 mi" (G2 rows). '' for a missing distance. */
export const getMilesLabel = (distance) => {
  const miles = parseFloat(distance);
  return Number.isNaN(miles) ? '' : `${miles.toFixed(1)} mi`;
};

/** "GoLocker · 230 Park Ave" — the bank's street, falling back to its name. */
export const getGoLockerLocationName = (location) => {
  const street = location?.address?.address;
  return street ? `${GOLOCKER_BRAND.name} · ${street}` : (location?.name || GOLOCKER_BRAND.name);
};

/** "Find 3 GoLockers within 1 mi" (G1). */
export const getNearbyGoLockersLabel = (count, radiusMiles = GOLOCKER_DEFAULT_RADIUS_MILES) => (
  `Find ${count} ${GOLOCKER_BRAND.name}${count === 1 ? '' : 's'} within ${radiusMiles} mi`
);

/**
 * The list header for the live map rows:
 *   all full          → E6 "All 3 nearby GoLockers are full" + "Live"
 *   request failed,
 *   or no bank answered → E4 "Couldn't load · Retry"
 *   loaded            → G2 "Live · updated just now"
 *   loading           → no status yet (rows render from the card's preview)
 */
export const getLiveHeaderState = ({
  loading, error, locations = [], allFull, radiusMiles = GOLOCKER_DEFAULT_RADIUS_MILES,
}) => {
  const defaultTitle = `${GOLOCKER_BRAND.name}s within ${radiusMiles} mi`;
  if (loading) return { title: defaultTitle, status: null };
  const noneAnswered = locations.length > 0 && locations.every(
    (location) => !location.availability
      || location.availability.status === AVAILABILITY_STATUS.UNKNOWN,
  );
  if (error || noneAnswered) return { title: defaultTitle, status: 'error' };
  if (allFull) {
    return {
      title: `All ${locations.length} nearby ${GOLOCKER_BRAND.name}${locations.length === 1 ? ' is' : 's are'} full`,
      status: 'live',
    };
  }
  return { title: defaultTitle, status: 'live' };
};

/** The tip under the map, per state (G2 / E4 / E6). */
export const getLiveTip = (headerStatus, allFull) => {
  if (allFull) {
    return 'Lockers are usually emptied within 10–12 hours. You can still choose GoLocker and check again before you go, or pick another option below.';
  }
  if (headerStatus === 'error') {
    return 'Any GoLocker still works. Check live availability again before you go.';
  }
  return 'Any GoLocker works — no need to pick one.';
};

/**
 * The kiosk steps (G3 single bag / E3 multi-bag). Returned as two runs because the
 * bag illustration sits between step 3 and step 4.
 *
 * `hasQrAbove`: whether the screen actually shows the QR to scan (bag 1's, for a
 * batch). When it does, step 3 leads with it and `bagHint` is the backup line that
 * reveals the illustration. When it doesn't (labels still failing, PDF-only, no QR
 * image), step 3 points at the bag and there is no hint: the illustration is shown.
 */
export const getKioskSteps = (bagCount = 1, hasQrAbove = true) => {
  const isMulti = bagCount > 1;
  const scanAbove = isMulti ? 'Scan bag 1’s QR above' : 'Scan the QR above';
  const scanBag = isMulti ? 'Scan the QR code on bag 1' : 'Scan the QR code on your bag';
  return {
    before: [
      'Tap Return on the kiosk screen',
      isMulti ? `Enter ${bagCount} when it asks how many bags` : 'Enter how many bags you’re dropping off',
      hasQrAbove ? scanAbove : scanBag,
    ],
    bagHint: hasQrAbove ? 'You can also scan the QR code on your bag.' : null,
    after: [
      'Pick a size. Medium fits most bags; if it’s tight, go bigger',
      isMulti ? 'Close the door, then repeat for each bag' : 'Put the bag in and close the door. It won’t reopen',
    ],
    note: isMulti
      ? `Scan each bag separately: ${bagCount} bags, ${bagCount} lockers.`
      : 'Scan each bag separately. One bag per locker.',
  };
};

/** The "before you go" tip above Check Live Availability (G3 / E3). */
export const getBeforeYouGoTip = (bagCount = 1) => (
  bagCount > 1
    ? `You’ll need ${bagCount} free lockers. Check availability before you go.`
    : 'Lockers fill up during the day. Check availability before you go.'
);

// "Kiosk problem? Call the number on the kiosk" (G3/E3 Need Help) — GoLocker's own
// FAQs, an external site (opened in a new tab like the app's other external links).
export const GOLOCKER_KIOSK_HELP_URL = 'https://www.golocker.com/faqs';

export const REWARD_STATUS = { PENDING: 'PENDING', ADDED: 'ADDED' };

/**
 * The bank a drop-off was recorded at, as one display string: its street when the
 * webhook could resolve it, else its name, else ''. `address` arrives as the bank's
 * street string from R4; an object with `.address` is accepted too, like a mapped
 * location.
 */
export const getDropOffPlace = (location) => {
  if (!location) return '';
  const { address, name } = location;
  const street = typeof address === 'string' ? address : address?.address;
  return street || name || '';
};

/** "GoLocker, 230 Park Ave" — or just "GoLocker" when the bank could not be named. */
export const getDropOffPlaceLabel = (location) => {
  const place = getDropOffPlace(location);
  return place ? `${GOLOCKER_BRAND.name}, ${place}` : GOLOCKER_BRAND.name;
};

/** "2:41 PM". '' for a missing/invalid timestamp. */
export const formatDropOffTime = (iso) => {
  const time = moment(iso);
  return iso && time.isValid() ? time.format('h:mm A') : '';
};

/** "Today" / "Yesterday" / "Oct 18". */
export const formatDropOffDay = (iso) => {
  const time = moment(iso);
  if (!iso || !time.isValid()) return '';
  if (time.isSame(moment(), 'day')) return 'Today';
  if (time.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return time.format('MMM D');
};

/**
 * "******451234" — the G6 frame's bag id: six masks and the last six of the code's
 * numeric part (the same shape the register header uses).
 */
export const maskGoLockerBagId = (code) => {
  const [numericPart] = String(code ?? '').split('-');
  return numericPart.length > 6 ? `******${numericPart.slice(-6)}` : '';
};

/**
 * Which G5 frame a recorded drop-off shows — drop-off PROGRESS, not the reward:
 *   'delivered'  — deliveredAt set or shipmentStatus DELIVERED (12349:12098)
 *   'pickedUp'   — the bag left the locker (dropOff.pickedUpAt) (12349:12473)
 *   'droppedOff' — still in the locker (12245:3411)
 */
export const G5_STATES = { DROPPED_OFF: 'droppedOff', PICKED_UP: 'pickedUp', DELIVERED: 'delivered' };

export const isShipmentDelivered = ({ deliveredAt, shipmentStatus }) => (
  Boolean(deliveredAt) || String(shipmentStatus ?? '').toUpperCase() === 'DELIVERED'
);

export const getDroppedOffState = ({ dropOff, deliveredAt, shipmentStatus }) => {
  if (isShipmentDelivered({ deliveredAt, shipmentStatus })) return G5_STATES.DELIVERED;
  if (dropOff?.pickedUpAt) return G5_STATES.PICKED_UP;
  return G5_STATES.DROPPED_OFF;
};

export const G5_TITLES = {
  [G5_STATES.DROPPED_OFF]: 'Dropped off',
  [G5_STATES.PICKED_UP]: 'Picked up',
  [G5_STATES.DELIVERED]: 'Delivered!',
};

/** Whether the bag's TrashieCash was released (RECEIVED, normally — so from Dropped off on). */
export const isRewardAdded = (reward) => reward?.status === REWARD_STATUS.ADDED;

/**
 * The TrashieCash amount, "+$2.00" — only once the reward is ADDED and the API knows
 * it. A pending reward (and G6) shows the coin with no number.
 */
export const getRewardAmountLabel = (reward) => {
  if (reward?.status !== REWARD_STATUS.ADDED) return null;
  const amount = Number(reward?.amount);
  return Number.isFinite(amount) && amount > 0 ? `+${formatCurrencyNumber(amount)}` : null;
};

const bagsLabel = (bagCount = 1) => `${bagCount} bag${bagCount === 1 ? '' : 's'}`;

/** G5 subline: "1 bag · GoLocker, 230 Park Ave · 2:41 PM". */
export const getDroppedOffSubline = ({ bagCount = 1, dropOff }) => [
  bagsLabel(bagCount),
  getDropOffPlaceLabel(dropOff?.location),
  formatDropOffTime(dropOff?.droppedOffAt),
].filter(Boolean).join(' · ');

/** G6 subline: "1 bag · Bag id ******451234" (a batch names no single bag). */
export const getPendingSubline = ({ bagCount = 1, code }) => {
  const bagId = maskGoLockerBagId(code);
  return bagCount === 1 && bagId ? `${bagsLabel(1)} · Bag id ${bagId}` : bagsLabel(bagCount);
};

/**
 * G5 "What happens next", copy as Figma (12245:3411 / 12349:12473 / 12349:12098).
 * Step 1 is done in every G5 state, step 2 from Picked up on, step 3 only when
 * Delivered!. With no droppedOffAt (pickup arrived, drop-off didn't) the time is left
 * out. Step 3's subtext is Figma's verbatim (a leftover, flagged to design).
 */
export const getDroppedOffTimeline = ({ dropOff, deliveredAt, shipmentStatus }) => {
  const state = getDroppedOffState({ dropOff, deliveredAt, shipmentStatus });
  const when = [formatDropOffDay(dropOff?.droppedOffAt), formatDropOffTime(dropOff?.droppedOffAt)]
    .filter(Boolean).join(', ');
  return [
    {
      key: 'dropped',
      done: true,
      title: 'Dropped off',
      body: ['Door closed', when, getDropOffPlaceLabel(dropOff?.location)].filter(Boolean).join(' · '),
    },
    {
      key: 'picked',
      done: state !== G5_STATES.DROPPED_OFF,
      title: 'Picked up from the locker',
      body: 'Usually within 24–48 hrs · no Sunday pickups',
    },
    {
      key: 'delivered',
      done: state === G5_STATES.DELIVERED,
      title: 'Delivered',
      body: 'We’ll let you know when your bag reaches sorting',
    },
  ];
};

/** G6 timeline (12245:3489). Figma's "[GoLocker location]" is unknown before the drop-off. */
export const PENDING_TIMELINE = [
  {
    key: 'waiting',
    icon: 'clock',
    title: 'Dropped off · Waiting on locker',
    body: 'Today · any GoLocker location',
  },
  {
    key: 'confirmed',
    title: 'Confirmed by GoLocker',
    body: 'As soon as the locker reconnects',
  },
  {
    key: 'cash',
    title: 'Trashie Cash added',
    body: 'On confirmation, or at our warehouse at the latest',
  },
];

export const PENDING_SYNC_COPY = {
  title: 'Lockers can take a while to sync',
  // Figma breaks before the last sentence.
  body: 'If the locker was offline when you closed the door, it sends your drop-off once it reconnects. Your Trashie Cash is added once your bag is confirmed dropped off or when it reaches the warehouse at the latest.\nYou don’t need to do anything.',
};

/* LIVE STATUS (G5/G6 polling) ---------------------------------------------- */

// Progress rank of one bag: no drop-off (G6) < Dropped off < Picked up < Delivered!.
// Delivery counts even with no drop-off on record (the webhook was missed).
const progressRank = (bag) => {
  if (!bag) return 0;
  if (isShipmentDelivered(bag)) return 3;
  if (!bag.dropOff) return 0;
  return { droppedOff: 1, pickedUp: 2 }[getDroppedOffState(bag)];
};

/** Whether a bag has moved past G6: dropped off, picked up or delivered. */
export const hasDropOffProgress = (bag) => progressRank(bag) > 0;

/**
 * One bag's live status off GET /redemptions/:code — `dropOff`, `reward` at the top
 * level, delivery on `shipment`.
 */
export const toBagDropOffStatus = (data, code) => ({
  code: code ?? data?.code ?? null,
  bagCount: 1,
  dropOff: data?.dropOff ?? null,
  reward: data?.reward ?? null,
  deliveredAt: data?.shipment?.deliveredAt ?? data?.deliveredAt ?? null,
  shipmentStatus: data?.shipment?.shipmentStatus ?? data?.shipmentStatus ?? null,
});

/**
 * A batch's live status off GET /redemptions/bulk/:batchId. The screen shows one
 * state, so the batch reads as its LEAST-advanced dropped-off bag: "Delivered!" only
 * once every dropped-off bag is delivered, G6 while none has been dropped off yet.
 * The reward is ADDED once every dropped-off bag's is, with their amounts summed.
 */
export const toBatchDropOffStatus = (data) => {
  const items = Array.isArray(data?.items) ? data.items : [];
  const bags = items.map((item) => toBagDropOffStatus(item, item?.code));
  const dropped = bags.filter(hasDropOffProgress);
  const bagCount = data?.labelCount ?? items.length ?? 1;
  if (dropped.length === 0) {
    return {
      code: null, bagCount, dropOff: null, reward: null, deliveredAt: null, shipmentStatus: null,
    };
  }
  const least = dropped.reduce((a, b) => (progressRank(b) < progressRank(a) ? b : a));
  const allAdded = dropped.every((bag) => isRewardAdded(bag.reward));
  const amount = dropped.reduce((sum, bag) => sum + (Number(bag.reward?.amount) || 0), 0);
  return {
    ...least,
    code: null,
    bagCount,
    reward: allAdded
      ? { status: REWARD_STATUS.ADDED, amount: amount > 0 ? amount : null }
      : { status: REWARD_STATUS.PENDING, amount: null },
  };
};

export const isDropOffStatusFinal = (status) => progressRank(status) === 3;

/** Comparable fingerprint of what the screen shows, to tell a change from a repeat. */
export const dropOffStatusKey = (status) => JSON.stringify([
  progressRank(status),
  status?.reward?.status ?? null,
  status?.reward?.amount ?? null,
  status?.dropOff?.location?.locationId ?? null,
]);

// The G5/G6 screen when its status can't be loaded (not a Figma frame: no answer is not
// "waiting on the locker", so it must not borrow G6's copy).
export const STATUS_LOAD_ERROR_COPY = {
  title: "We couldn't load your drop-off status",
  body: 'Check your connection and try again.',
};

// Polling cadence (addendum 4): fast while waiting for the kiosk's webhook, slow once
// dropped off (pickup and delivery take hours), give up on G6 after 30 quiet minutes.
export const GOLOCKER_STATUS_POLL = {
  pendingMs: 15 * 1000,
  droppedOffMs: 60 * 1000,
  pendingGiveUpMs: 30 * 60 * 1000,
  // Consecutive failed fetches, with no status yet, before the screen stops showing the
  // loader and offers Retry (≈30s: the first fetch plus two 15s polls).
  failuresBeforeError: 3,
};

/* DROPPED-OFF LABEL CARD (Type3 12373:6840 / Type4 12373:6993) ---------------- */

/**
 * "Latest update:" per G5 state. Only "Picked up by carrier" is Figma's; the other
 * two are ours until design confirms them.
 */
export const LATEST_UPDATE_LABELS = {
  [G5_STATES.DROPPED_OFF]: 'Dropped off at the locker',
  [G5_STATES.PICKED_UP]: 'Picked up by carrier',
  [G5_STATES.DELIVERED]: 'Delivered',
};

/**
 * A /shipping-labels entry's GoLocker status, single or batch, in the same shape the
 * G5/G6 screen reads — so the card and the screen agree on the state.
 */
export const getEntryDropOffStatus = (entry) => {
  const items = Array.isArray(entry?.items) ? entry.items : [];
  const isBatch = entry?.type === 'batch' || (entry?.labelCount ?? items.length) > 1;
  return isBatch
    ? toBatchDropOffStatus(entry)
    : toBagDropOffStatus(items[0] ?? {}, items[0]?.code);
};

/** Whether a GoLocker list entry shows the dropped-off card: any bag past G6. */
export const isGoLockerEntryDroppedOff = (entry) => {
  const items = Array.isArray(entry?.items) ? entry.items : [];
  return items.some((item) => isGoLocker(item) && hasDropOffProgress(toBagDropOffStatus(item)));
};

/** "Latest update:" value for a status (the batch reads as its least-advanced bag). */
export const getLatestUpdateLabel = (status) => (
  LATEST_UPDATE_LABELS[getDroppedOffState(status ?? {})]
);

/**
 * The card's footer: "Dropped off on Oct 29" (droppedOffAt, else pickedUpAt, else
 * nothing) and the locker's directions (accessInstructions, else its street).
 */
export const getDroppedOffFooter = (status) => {
  const when = status?.dropOff?.droppedOffAt || status?.dropOff?.pickedUpAt;
  const time = moment(when);
  const location = status?.dropOff?.location;
  return {
    dateLabel: when && time.isValid() ? `Dropped off on ${time.format('MMM D')}` : '',
    directions: location?.accessInstructions
      || getDropOffPlace(location ? { address: location.address } : null),
  };
};
